import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { usePinterestBoards } from '@/utils/hooks/swr/usePinterestBoards';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';

import { PinterestBoardType } from '@/lib/pinterestBoards/types';

import Loader from '@/components/base/Loader';
import { Select } from '@/components/base/Select/SelectComponent';
import FilterSectionTitle from '@/components/pages/myprofile/filters/FilterSectionTitle';

export function SelectPinterestBoardFilter({ resetFilters, stopResetFilters, setRequestParams }) {
    const { activeSocialProfile } = useActiveSocialProfile();
    const { dataPinterestBoards } = usePinterestBoards({ activeSPID: activeSocialProfile?.id });

    const [isLoading, setIsLoading] = useState(false);
    const [pinterestBoards, setPinterestBoards] = useState<PinterestBoardType[]>([]);
    const [selectedBoard, setSelectedBoard] = useState(null);

    const router = useRouter();
    const { boardFilter } = router.query;
    const { deleteQuery } = useRouterQuery();

    const { t } = useTranslation('commons');

    const DEFAULT_BOARD_SELECTION = {
        value: t('media.boards.select_board'),
        label: t('media.boards.select_board'),
        id: 'select-board'
    } as const;

    useEffect(() => {
        if (boardFilter) {
            const newUrl = deleteQuery(['boardFilter']);
            router.replace(newUrl);
        }
    }, [boardFilter]);

    useEffect(() => {
        const hasBoards = dataPinterestBoards?.length > 0;
        setIsLoading(!hasBoards);
        if (hasBoards) {
            setPinterestBoards(dataPinterestBoards[0].data);
        }
    }, [dataPinterestBoards]);

    useEffect(() => {
        setRequestParams((prevState) => {
            if (selectedBoard) {
                return {
                    ...prevState,
                    board_id: selectedBoard.id
                };
            }
            delete prevState.board_id;
            return { ...prevState };
        });
    }, [selectedBoard, setRequestParams]);

    useEffect(() => {
        if (resetFilters) {
            handleSelectedBoard(null);
            stopResetFilters();
        }
    }, [resetFilters]);

    function handleSelectedBoard(board) {
        setSelectedBoard(board);
    }

    return (
        <>
            {isLoading ? (
                <Loader />
            ) : (
                <div>
                    <FilterSectionTitle icon="albums">{t('media.boards.title')}</FilterSectionTitle>
                    <Select
                        data-testid="select-board-filter"
                        options={pinterestBoards.map((board) => ({
                            value: board.name,
                            label: board.name,
                            id: board.id
                        }))}
                        selected={selectedBoard ?? DEFAULT_BOARD_SELECTION}
                        setSelected={handleSelectedBoard}
                        disabled={false}
                    />
                </div>
            )}
        </>
    );
}
